import { bunja } from 'bunja';

import AbstractSocket from '@flipster/socket/AbstractSocket';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { EventEmitter } from '@flipster/utils/event-emitter';

import { r230522NormalSubSocketConnectionBunja, weakR230522NormalSubSocketBunja } from '.';
import { ClientMessageJsonValue } from '../../model/prex.v2.r230522.messages.default.client';
import { ServerMessageJsonValue } from '../../model/prex.v2.r230522.messages.default.server';
import { V2SocketEvents } from '../../original/socket';
import { weakR230522NormalSubTablesBunja, type AccumulatedTables } from './accumulator';
import { weakR230522NormalSubStateBunja } from './subscription';

export const r230522FixedSubEmulatorBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { r230522NormalSubSocket } = bunja.use(weakR230522NormalSubSocketBunja);
  const { createHandle, createMultiTopicHandle } = bunja.use(weakR230522NormalSubStateBunja);
  const { tablesAtom } = bunja.use(weakR230522NormalSubTablesBunja);
  const { connect, close } = bunja.use(r230522NormalSubSocketConnectionBunja);

  const r230522FixedSubSocket = new EventEmitter() as AbstractSocket<ClientMessageJsonValue, V2SocketEvents>;
  Object.defineProperty(r230522FixedSubSocket, 'state', { get: () => r230522NormalSubSocket.state });
  Object.defineProperty(r230522FixedSubSocket, 'debugLabel', { get: () => 'emulated-r230522-fixed-sub' });
  r230522FixedSubSocket.connect = () => void connect();
  r230522FixedSubSocket.close = close;

  r230522FixedSubSocket.send = ({ c, s }) => {
    const q = `${Date.now()}000000`;
    if (c) return r230522NormalSubSocket.send({ q, c });
    if (s?.currencies) currenciesHandle.set({ rows: sanitizeRows(s.currencies) });
    if (s?.intervalData) intervalDataHandle.set({ rows: sanitizeRows(s.intervalData) });
    if (s?.orderBook) orderBookHandle.set({ rows: sanitizeRows(s.orderBook) });
    if (s?.symbol) symbolHandle.set({ rows: sanitizeRows(s.symbol) });
    if (s?.tickers) tickersHandle.set({ rows: sanitizeRows(s.tickers) });
  };

  function sanitizeRows(rows?: string[] | string | null): string[] {
    if (!rows) return [];
    if (typeof rows === 'string') return [rows];
    return rows;
  }

  const currenciesHandle = createHandle('market/currency-details');
  const symbolHandle = createMultiTopicHandle(['market/pswap-details', 'market/spot-details']);
  const tickersHandle = createMultiTopicHandle(['market/tickers', 'market/spot-tickers']);
  const intervalDataHandle = createHandle('market/interval-data');
  const orderBookHandle = createHandle('market/orderbooks-v2');
  const fixedTopicsHandle = createMultiTopicHandle(
    [
      'private/account',
      'private/margins',
      'market/pswaps',
      'private/orders',
      'private/positions',
      'private/pswaps',
      'market/spots',
      'private/spot-orders',
      'private/spots',
      'private/wallets',
    ],
    { rows: ['*'] },
  );
  const everyHandle = [
    currenciesHandle,
    symbolHandle,
    tickersHandle,
    intervalDataHandle,
    orderBookHandle,
    fixedTopicsHandle,
  ];
  bunja.effect(() => () => everyHandle.forEach((handle) => handle.dispose()));

  const offStateChanged = r230522NormalSubSocket.on('stateChanged', (state) => {
    r230522FixedSubSocket.emit('stateChanged', state);
    r230522FixedSubSocket.emit(state, undefined);
  });
  const offSessionExpired = r230522NormalSubSocket.on('sessionExpired', (state) => {
    r230522FixedSubSocket.emit('sessionExpired', state);
  });
  const offError = r230522NormalSubSocket.on('error', (error) => {
    r230522FixedSubSocket.emit('error', error);
  });
  const offMessage = r230522NormalSubSocket.on('message', (data) => {
    const fixSubServerMessage: ServerMessageJsonValue = { p: data.p, q: data.q };
    let receive = false;
    try {
      // 다른 모든 토픽들은 스냅샷만 가지고도 작동하지만 (아래 tablesAtom를 store.sub 하는 코드 참고)
      // market/interval-data는 특이하게 차트에서 insert와 update 메시지를 직접 처리하므로 그대로 흘려줘야 함
      if (data.t?.['market/interval-data']) {
        fixSubServerMessage.t = { 'market/interval-data': data.t['market/interval-data'] };
        receive = true;
      }
      // 커맨드 응답이나 이벤트가 없으면 메시지 무시
      if (!('c' in data || 'e' in data)) return;
      if ('c' in data) fixSubServerMessage.c = data.c;
      if ('e' in data) fixSubServerMessage.e = data.e;
      receive = true;
    } finally {
      if (receive) r230522FixedSubSocket.emit('message', fixSubServerMessage);
    }
  });
  bunja.effect(() => () => {
    offStateChanged();
    offSessionExpired();
    offError();
    offMessage();
  });
  let prevTables = {} as AccumulatedTables;
  bunja.effect(() =>
    store.sub(tablesAtom, () => {
      const p = `${Date.now()}000000`;
      const tables = store.get(tablesAtom);
      const t: Record<string, { s: unknown }> = {};
      for (const topic of Object.keys(tables) as (keyof AccumulatedTables)[]) {
        if (tables[topic] !== prevTables[topic]) {
          t[topic] = { s: tables[topic] };
        }
      }
      prevTables = tables;
      if (Object.keys(t).length > 0) {
        r230522FixedSubSocket.emit('message', { t, p });
      }
    }),
  );

  return { r230522FixedSubSocket };
});
r230522FixedSubEmulatorBunja.debugLabel = 'r230522FixedSubEmulatorBunja';
